/**
 * Feedback Form component for collecting user feedback on recommendations
 */

import { AlertCircle, CheckCircle, Loader2, Send, Star } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThumbsRating } from '../types';

interface BookRating {
  isbn: string;
  title: string;
  rating: ThumbsRating;
  is_gift?: boolean | null;
}

interface ProfileRating {
  name: string;
  rating: ThumbsRating;
}

interface FeedbackFormProps {
  customerId?: string;
  bookRatings: Record<string, ThumbsRating>;
  books: Array<{
    isbn?: string;
    sku?: string;
    book_id?: string;
    title?: string;
  }>;
  historyRatings?: Record<string, ThumbsRating>;
  historyBooks?: Array<{
    isbn?: string;
    sku?: string;
    book_id?: string;
    title?: string;
  }>;
  giftStatus?: Record<string, boolean | null>;
  profileRatings?: Record<string, ThumbsRating>;
  profiles?: Array<{
    name?: string;
  }>;
  onSubmit: (feedback: FeedbackData) => Promise<void>;
  isSubmitting?: boolean;
  submitError?: string | null;
  submitSuccess?: boolean;
}

export interface FeedbackData {
  customer_id?: string;
  overall_rating: number;
  comments: string;
  book_ratings: BookRating[];
  history_book_ratings: BookRating[];
  profile_ratings: ProfileRating[];
  timestamp: string;
}

const StarRating: React.FC<{
  rating: number;
  onRatingChange: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
}> = ({ rating, onRatingChange, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);

  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-7 h-7 sm:w-8 sm:h-8',
    lg: 'w-8 h-8 sm:w-10 sm:h-10',
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRatingChange(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  customerId,
  bookRatings,
  books,
  historyRatings = {},
  historyBooks = [],
  giftStatus = {},
  profileRatings = {},
  profiles = [],
  onSubmit,
  isSubmitting = false,
  submitError = null,
  submitSuccess = false,
}) => {
  const { t } = useTranslation();
  const [overallRating, setOverallRating] = useState(0);
  const [comments, setComments] = useState('');

  const getBookId = (
    book: { isbn?: string; sku?: string; book_id?: string },
    index: number = 0
  ): string => {
    return book.book_id || book.isbn || book.sku || `book-${index}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Convert book ratings to array format (recommendations - NO gift status)
    // Use original index from books, not filtered index
    const bookRatingsArray: BookRating[] = books
      .map((book, originalIndex) => ({ book, originalIndex }))
      .filter(({ book, originalIndex }) => {
        const bookId = getBookId(book, originalIndex);
        return bookRatings[bookId] !== undefined && bookRatings[bookId] !== null;
      })
      .map(({ book, originalIndex }) => {
        const bookId = getBookId(book, originalIndex);
        return {
          isbn: bookId,
          title: book.title || 'Unknown',
          rating: bookRatings[bookId] ?? null,
        };
      });

    // Convert history book ratings to array format (history - INCLUDE gift status)
    // Include books that have either a rating OR a gift status
    const historyRatingsArray: BookRating[] = historyBooks
      .map((book, originalIndex) => ({ book, originalIndex }))
      .filter(({ book, originalIndex }) => {
        const bookId = getBookId(book, originalIndex);
        const hasRating = historyRatings[bookId] !== undefined && historyRatings[bookId] !== null;
        const hasGiftStatus = bookId in giftStatus && giftStatus[bookId] !== null;
        return hasRating || hasGiftStatus;
      })
      .map(({ book, originalIndex }) => {
        const bookId = getBookId(book, originalIndex);
        const giftValue = bookId in giftStatus ? giftStatus[bookId] : null;
        return {
          isbn: bookId,
          title: book.title || 'Unknown',
          rating: historyRatings[bookId] || null,
          is_gift: giftValue,
        };
      });

    // Convert profile ratings to array format
    const profileRatingsArray: ProfileRating[] = profiles
      .filter((profile) => {
        const profileName = profile.name || '';
        return profileRatings[profileName] !== undefined && profileRatings[profileName] !== null;
      })
      .map((profile) => {
        const profileName = profile.name || 'Unknown';
        return {
          name: profileName,
          rating: profileRatings[profileName] ?? null,
        };
      });

    const feedbackData: FeedbackData = {
      customer_id: customerId,
      overall_rating: overallRating,
      comments,
      book_ratings: bookRatingsArray,
      history_book_ratings: historyRatingsArray,
      profile_ratings: profileRatingsArray,
      timestamp: new Date().toISOString(),
    };

    await onSubmit(feedbackData);
  };

  if (submitSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-green-800 mb-2">{t('feedback.successTitle')}</h3>
        <p className="text-green-600">{t('feedback.successBody')}</p>
      </div>
    );
  }

  const ratingLabels: Record<number, string> = {
    1: t('feedback.rating1'),
    2: t('feedback.rating2'),
    3: t('feedback.rating3'),
    4: t('feedback.rating4'),
    5: t('feedback.rating5'),
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Overall Rating */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
        <p className="block text-base sm:text-lg font-semibold text-gray-800 mb-3">
          {t('feedback.overallQuestion')}
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <StarRating rating={overallRating} onRatingChange={setOverallRating} size="lg" />
          <span className="text-sm sm:text-base text-gray-600">
            {overallRating > 0 ? (
              <span className="font-medium text-indigo-brand-black">
                {ratingLabels[overallRating]}
              </span>
            ) : (
              t('feedback.clickToRate')
            )}
          </span>
        </div>
      </div>

      {/* Comments */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
        <label
          htmlFor="feedback-comments"
          className="block text-base sm:text-lg font-semibold text-gray-800 mb-3"
        >
          {t('feedback.commentsLabel')}
        </label>
        <textarea
          id="feedback-comments"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder={t('feedback.commentsPlaceholder')}
          rows={4}
          className="w-full px-4 py-3 border border-indigo-brand-border rounded-sm focus:ring-2 focus:ring-indigo-brand-black/20 focus:border-indigo-brand-black resize-none"
        />
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">{t('feedback.submitFailed')}</h4>
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting || overallRating === 0}
        className={`w-full flex items-center justify-center gap-2 py-4 px-6 font-semibold text-lg transition-colors ${
          isSubmitting || overallRating === 0
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-indigo-brand-black text-white hover:bg-black'
        }`}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('feedback.submitting')}
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {t('feedback.submitButton')}
          </>
        )}
      </button>

      {overallRating === 0 && (
        <p className="text-center text-sm text-gray-500">{t('feedback.ratingRequired')}</p>
      )}
    </form>
  );
};

export default FeedbackForm;
